import { Brand } from '@@types'
import { useEffect, useMemo, useState } from 'react'

import { useAddressStore } from '@entities/Address'
import { useDeliveryStore } from '@entities/DeliveryZone/model/useDeliveryStore'

import { cacheBrandIcon, getCachedBrandIcon } from '@shared/lib/brandIconCache'

const pickBrand = (
  brandFromProps?: Brand | null,
  storeBrand?: Brand | null,
  currentZoneBrand?: Brand | null,
  firstAvailableBrand?: Brand | null,
) => brandFromProps
  || storeBrand
  || currentZoneBrand
  || firstAvailableBrand

export const useBrandSquareLogo = (brandFromProps?: Brand | null) => {
  const storeBrand = useAddressStore((state) => state.brand)
  const currentZoneBrand = useDeliveryStore((state) => state.currentZone?.shop?.brand ?? null)
  const availableBrands = useDeliveryStore((state) => state.availableBrands)

  const brand = useMemo(
    () => pickBrand(
      brandFromProps,
      storeBrand,
      currentZoneBrand,
      availableBrands[0]?.brandInfo ?? null,
    ),
    [ brandFromProps, storeBrand, currentZoneBrand, availableBrands ],
  )

  const brandId = brand?.id ? String(brand.id) : null
  const iconUrl = brand?.squareLogo?.url ?? null

  const [ iconSrc, setIconSrc ] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    if (!brandId || !iconUrl) {
      setIconSrc(null)
      return () => {
        cancelled = true
      }
    }

    const cached = getCachedBrandIcon(brandId, iconUrl)
    if (cached) {
      setIconSrc(cached)
    }
    else {
      setIconSrc(iconUrl)
    }

    cacheBrandIcon(brandId, iconUrl)
      .then((src) => {
        if (!cancelled && src) {
          setIconSrc(src)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIconSrc(iconUrl)
        }
      })

    return () => {
      cancelled = true
    }
  }, [ brandId, iconUrl ])

  return iconSrc
}
